'use client';

import { useEffect, useState } from 'react';

export interface CurrentMonthScope {
  year: number;
  /** 1-based, matching the overview endpoint's `month` param. */
  month: number;
  from: Date;
  to: Date;
}

export function currentMonthScope(now = new Date()): CurrentMonthScope {
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    from: new Date(now.getFullYear(), now.getMonth(), 1),
    to: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999),
  };
}

// setTimeout delays overflow past 2^31−1 ms (~24.8 days) and would fire immediately,
// while a month is up to ~31 days away — so waits are clamped and chained.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/**
 * The current calendar month as a stable scope object, re-derived at the next local
 * month boundary — a tab left open (or restored) across the turn of the month rolls
 * over instead of keeping the stale SWR key and drawer range alive.
 */
export function useCurrentMonthScope(): CurrentMonthScope {
  const [scope, setScope] = useState(currentMonthScope);
  // Bumped when a clamped wait fires inside the same month, so the effect reschedules.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    // scope.month is 1-based, so as a 0-based month index it IS the next month.
    const boundary = new Date(scope.year, scope.month, 1).getTime();
    const delay = Math.min(Math.max(boundary - Date.now(), 0) + 1000, MAX_TIMEOUT_MS);
    const timer = setTimeout(() => {
      const next = currentMonthScope();
      if (next.year !== scope.year || next.month !== scope.month) setScope(next);
      else setTick((t) => t + 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [scope, tick]);

  return scope;
}
